import React, { useEffect, useMemo, useState } from "react";
import { Segmented, Tooltip } from "antd";
import { useColumnSizing } from "./useColumnSizing";
import {
  COLUMN_PHASES,
  isActionColumn,
  isIdentityColumn,
  orderSiteVisitColumns,
  siteVisitColumnMeta,
} from "./siteVisitColumnOrder";
import "./siteVisitColumns.scss";

/**
 * Canonical column order plus a collapse control, for any site-visit table.
 *
 * Later in the lifecycle a table carries more history than anyone needs at a
 * glance: by the reporting boards, the planning columns are settled facts and
 * the phase's own columns are what people are actually working in. So each table
 * declares the handful of columns worth opening with (`defaultKeys`), and
 * everything else collapses behind one toggle instead of being dropped — the
 * data is still one click away, and the order never changes when it comes back.
 *
 * Identity columns (identifier, name, status) and the row-action column are
 * never hidden by this toggle; they anchor every table. (The action column can
 * still be *folded* to its icon — that is useColumnSizing, and it keeps the
 * actions reachable from a popover rather than taking them away.)
 *
 * `defaultKeys` are canonical keys — pass "identifier", not
 * "siteVisitCardIdentifier"; the alias table resolves whatever spelling the
 * table itself uses.
 *
 * Omit `defaultKeys` (the Planning board) and nothing collapses: the table shows
 * every column it has and no toggle is rendered.
 */
const prefKey = (tableId) => `siteVisitColumns.${tableId}`;

const readPref = (tableId) => {
  try {
    return localStorage.getItem(prefKey(tableId)) === "all";
  } catch {
    return false;
  }
};

export const useSiteVisitColumns = ({ tableId, columns, defaultKeys }) => {
  const [showAll, setShowAll] = useState(() => readPref(tableId));

  useEffect(() => {
    try {
      localStorage.setItem(prefKey(tableId), showAll ? "all" : "key");
    } catch {
      // A blocked localStorage only costs the remembered choice.
    }
  }, [tableId, showAll]);

  const ordered = useMemo(() => orderSiteVisitColumns(columns), [columns]);

  // No default set means "show everything", so there is nothing to collapse.
  const collapsible = useMemo(() => {
    if (!defaultKeys) return [];
    return ordered.filter((column) => {
      if (isActionColumn(column) || isIdentityColumn(column)) return false;
      const canonical = siteVisitColumnMeta(column.key)?.key;
      // A column the order table doesn't know is table-specific — leave it on.
      if (!canonical) return false;
      return !defaultKeys.includes(canonical);
    });
  }, [ordered, defaultKeys]);

  const visibleColumns = useMemo(() => {
    if (showAll || !collapsible.length) return ordered;
    const hidden = new Set(collapsible.map((column) => column.key));
    return ordered.filter((column) => !hidden.has(column.key));
  }, [ordered, collapsible, showAll]);

  // Per-user column widths and folded columns, on top of the visible set.
  const {
    columns: sizedColumns,
    components,
    sizingBar,
  } = useColumnSizing({ tableId, columns: visibleColumns });

  const columnsBar =
    collapsible.length || sizingBar ? (
      <div className="siteVisitColumnsBar">
        {collapsible.length ? (
          <CollapsedColumnsToggle
            hidden={collapsible}
            showAll={showAll}
            onChange={(value) => setShowAll(value === "all")}
          />
        ) : null}
        {sizingBar}
      </div>
    ) : null;

  return {
    columns: sizedColumns,
    components,
    columnsBar,
    hiddenCount: collapsible.length,
  };
};

/** Lists what is collapsed, grouped by the phase the columns belong to. */
const CollapsedColumnsToggle = ({ hidden, showAll, onChange }) => {
  const byPhase = hidden.reduce((acc, column) => {
    const meta = siteVisitColumnMeta(column.key);
    const phase = COLUMN_PHASES[meta?.phase] || "Other";
    acc[phase] = [...(acc[phase] || []), meta?.label || column.key];
    return acc;
  }, {});

  return (
    <>
      <span className="siteVisitColumnsBar__label">Columns</span>
      <Tooltip
        title={
          <div>
            {Object.entries(byPhase).map(([phase, labels]) => (
              <div key={phase} style={{ marginBottom: 4 }}>
                <strong>{phase}:</strong> {labels.join(", ")}
              </div>
            ))}
          </div>
        }
      >
        <Segmented
          size="small"
          className="siteVisitColumnsBar__segmented"
          value={showAll ? "all" : "key"}
          onChange={onChange}
          options={[
            { label: "Key fields", value: "key" },
            { label: `All fields (+${hidden.length})`, value: "all" },
          ]}
        />
      </Tooltip>
    </>
  );
};

export default useSiteVisitColumns;
